import ForceGraph3D, { type LinkObject } from "3d-force-graph";
import type { ConceptWithDistances } from "@storytell/client";
import type { Asset, Concept, ConceptId } from "@storytell/types";
import { type Component, createEffect, createSignal, on, onCleanup, onMount } from "solid-js";
import type { SetStoreFunction } from "solid-js/store";
import { twMerge } from "tailwind-merge";
import { CSS2DObject, CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { useWire } from "~/wire";
import { getLabelOpacityUpdater } from "./updateLabelOpacity";

export const ConceptsGraph: Component<{
  concepts: Concept[];
  search: string;
  buildFilterFunction: (item: Asset | Concept) => boolean;
  onConceptClick: (id: ConceptId | "") => void;
  onGraphConceptClick: (id: ConceptId | "") => void;
  graphSelectedConcept: string;
  score: Readonly<{
    min: number;
    max: number;
    current: number;
  }>;
  setScore: SetStoreFunction<{
    min: number;
    max: number;
    current: number;
  }>;
}> = (props) => {
  let opacityInterval: ReturnType<typeof setInterval> | undefined;
  const wire = useWire();

  const [ref, setRef] = createSignal<HTMLDivElement>();

  // Track pointer down position to distinguish between clicks and drags
  const [pointerDownPos, setPointerDownPos] = createSignal<{ x: number; y: number } | null>(null);
  const CLICK_THRESHOLD = 5; // pixels - if pointer moves more than this, it's considered a drag

  // Store references to node elements for opacity updates
  const nodeElements = new Map<string, HTMLElement>();

  const [mounted, setMounted] = createSignal(false);
  onMount(() => {
    setTimeout(() => {
      setMounted(true);
    }, 100);
    onCleanup(() => {
      setMounted(false);
    });
  });

  // Cleanup interval on component unmount
  onCleanup(() => {
    if (opacityInterval) {
      clearInterval(opacityInterval);
    }
  });

  createEffect(
    on(
      () => ref() && mounted(),
      (mounted) => {
        if (!mounted) return;

        const r = ref();
        if (!r) return;

        const darkMode =
          document.documentElement.classList.contains("dark") || document.body.classList.contains("dark");

        const Graph = new ForceGraph3D(r, {
          extraRenderers: [new CSS2DRenderer()],
          rendererConfig: {
            powerPreference: "low-power",
          },
        })
          .cooldownTicks(100)
          .backgroundColor(darkMode ? "#1d2139" : "#f9fafb")
          .nodeColor(() => "#4567ff")
          .nodeThreeObject((node) => {
            const n = node as ConceptWithDistances;
            const nodeEl = document.createElement("div");
            const child = document.createElement("div");
            nodeEl.appendChild(child);
            child.textContent = n.name;
            child.className =
              "node-label bg-white dark:bg-black border-1 border-brand rounded-lg text-sm text-primary=text px-3 py-2 transition-all";
            // Store reference to this element for opacity updates
            nodeElements.set(n.id, nodeEl);
            return new CSS2DObject(nodeEl);
          })
          // .onNodeClick((node) => {
          //   console.log(node.id);
          // })
          // .onBackgroundClick(() => {
          //   props.onGraphConceptClick("");
          // })
          .nodeThreeObjectExtend(true)
          .nodeRelSize(1)
          .linkColor(() => "#9d69ff")
          .cameraPosition({ x: 0, y: 0, z: 170 })
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          .linkWidth((link: any) => {
            if (!props.graphSelectedConcept) return 0.15;
            if (link.source.id === props.graphSelectedConcept || link.target.id === props.graphSelectedConcept) {
              return 1;
            }
            return 0.1;
          })
          .width(r.clientWidth)
          .height(r.clientHeight);

        Graph.d3Force("link")?.distance((node: { distance: number }) => node.distance);

        const updateLabelOpacity = getLabelOpacityUpdater(Graph, nodeElements, () => props.score.current);

        // Create a resize observer for r, and setSize when it changes
        const resizeObserver = new ResizeObserver(() => {
          if (r) {
            Graph.width(r.clientWidth);
            Graph.height(r.clientHeight);
          }
        });
        resizeObserver.observe(r);

        onCleanup(() => {
          resizeObserver.disconnect();
        });

        // Update opacity on camera movement and engine stop
        Graph.onEngineStop(() => {
          updateLabelOpacity(true);
        });

        // Update opacity periodically to catch camera changes
        opacityInterval = setInterval(updateLabelOpacity, 100);

        // let angle = 0;
        // const distance = 200;
        // setInterval(() => {
        //   Graph.cameraPosition({
        //     x: distance * Math.sin(angle),
        //     z: distance * Math.cos(angle),
        //   });
        //   angle += Math.PI / 6000;
        // }, 10);

        createEffect(
          on(
            () => props.concepts,
            (concepts) => {
              if (concepts.length) {
                const sliced = concepts.map((c, index) => ({ ...c, index }));
                const exists = (id: string) => sliced.some((n) => n.id === id);

                props.setScore({
                  min: 0,
                  max: concepts.length,
                  current: Math.min(Math.round(concepts.length / 2), 20),
                });

                Graph.graphData({
                  nodes: sliced,
                  links:
                    sliced.flatMap((n) =>
                      Object.entries(n.distances || {})
                        .map(([target, distance]) => ({ source: n.id, target, distance: distance * 100 }))
                        .filter((l) => exists(l.source) && exists(l.target) && l.distance <= 75),
                    ) ?? [],
                });

                // Update opacity after graph data is set
                setTimeout(updateLabelOpacity, 100);
              }
            },
          ),
        );

        createEffect(
          on(
            () => props.graphSelectedConcept,
            (selectedId) => {
              Graph.refresh();

              if (!selectedId) {
                // Reset camera to center of graph
                Graph.cameraPosition(
                  { x: 0, y: 0, z: 100 }, // position slightly away from center
                  { x: 0, y: 0, z: 0 }, // look at the center
                  1500, // ms transition duration
                );
                return;
              }
              // Find the node in the current graph data
              const graphData = Graph.graphData();
              if (!graphData || !graphData.nodes) return;
              const node = graphData.nodes.find((n) => n.id === selectedId);
              if (!node) return;
              if (!node.x || !node.y || !node.z) return;

              // Aim at node from outside it
              const distance = 100;
              const distRatio = 1 + distance / Math.hypot(node.x, node.y, node.z);

              const newPos =
                node.x || node.y || node.z
                  ? { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio }
                  : { x: 0, y: 0, z: distance }; // special case if node is in (0,0,0)

              Graph.cameraPosition(
                newPos, // new position
                node as { x: number; y: number; z: number }, // lookAt ({ x, y, z })
                1500, // ms transition duration
              );
            },
          ),
        );
      },
    ),
  );

  // Helper function to check if pointer movement is within click threshold
  const isClick = (downPos: { x: number; y: number }, upPos: { x: number; y: number }) => {
    const deltaX = Math.abs(upPos.x - downPos.x);
    const deltaY = Math.abs(upPos.y - downPos.y);
    return deltaX <= CLICK_THRESHOLD && deltaY <= CLICK_THRESHOLD;
  };

  let sliderRef: HTMLDivElement | undefined = undefined;

  const sliderMove = (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (sliderRef) {
      const { height, top } = sliderRef.getBoundingClientRect();
      document.body.style.cursor = "grabbing";
      sliderRef.style.cursor = "grabbing";
      const percent = (e.clientY - top) / height;
      props.setScore({
        current: Math.max(props.score.min, Math.min(props.score.max, Math.round(percent * props.score.max))),
      });
    }
  };
  const pointerUp = () => {
    if (sliderRef) {
      document.body.style.removeProperty("cursor");
      sliderRef.style.removeProperty("cursor");
    }
    window.removeEventListener("pointermove", sliderMove);
    window.removeEventListener("pointerup", pointerUp);
  };

  return (
    <div class="relative h-full max-h-full">
      <div
        class={twMerge(
          "h-full max-h-full rounded-xl overflow-hidden w-full relative bg-panel",
          props.graphSelectedConcept && "rounded-r-none",
        )}
        ref={setRef}
        onPointerDown={(e: PointerEvent) => {
          setPointerDownPos({ x: e.clientX, y: e.clientY });
        }}
        onPointerUp={(e: PointerEvent) => {
          const downPos = pointerDownPos();
          if (downPos && isClick(downPos, { x: e.clientX, y: e.clientY })) {
            // This was a click, not a drag - handle the click
            const { clientX, clientY } = e;

            const closest = {
              id: "" as ConceptId | "",
              opacity: 0,
            };
            for (const [id, element] of nodeElements) {
              const c = element.children[0] as HTMLDivElement;
              if (!c) continue;
              const rect = c.getBoundingClientRect();
              if (clientX > rect.left && clientX < rect.right && clientY > rect.top && clientY < rect.bottom) {
                const opacity = Number(c.style.opacity);
                if (opacity > closest.opacity) {
                  closest.id = id as ConceptId | "";
                  closest.opacity = opacity;
                }
              }
            }

            if (closest.id) {
              props.onGraphConceptClick(closest.id);
            }
          }
          setPointerDownPos(null);
        }}
      />
      <div class="absolute top-2 bottom-0 right-2 text-xs text-white z-[50]">
        {/* <input
          type="range"
          onInput={(e) => setScore({ current: Number(e.target.value) })}
          value={score.current}
          min={score.min}
          class="w-32"
          max={score.max}
          step={1}
        /> */}

        <div class="flex flex-col gap-1 justify-center items-center select-none">
          <p class="text-xs text-violet-500 bg-canvas">High Level</p>
          <div
            class="relative cursor-grab"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              sliderRef = e.target as HTMLDivElement;
              sliderMove(e);
              window.addEventListener("pointermove", sliderMove);
              window.addEventListener("pointerup", pointerUp);
            }}
          >
            <div
              class="w-10 h-30 mx-auto bg-gradient-to-b from-violet-500/90 via-violet-500/25 to-violet-500/0 dark:from-violet-500/90 dark:via-violet-500/25 dark:to-violet-500/0"
              style="clip-path: polygon(50% 0%, 100% 100%, 0% 100%);"
            />
            <div
              class="text-xs text-primary-text absolute left-0 right-0 text-center pointer-events-none"
              style={{ top: `calc(${(props.score.current / props.score.max) * 100}% - 20px)` }}
            >
              {props.score.current === 0 ? "" : props.score.current}
            </div>
            <div
              class="w-8 h-1 bg-primary-text absolute left-1 pointer-events-none"
              style={{ top: `calc(${(props.score.current / props.score.max) * 100}% - 2px)` }}
            />
          </div>
          <p class="text-xs text-violet-500 bg-canvas">Detailed View</p>
        </div>
      </div>
    </div>
  );
};
